import { TWO_FACTOR_TYPE } from "@tastyworks/shared-utils/misc/constants"
import type { JsonHelper } from "../../tastyworks"
import {
  DEVICE_CHALLENGE_DESER,
  DeviceChallenge,
} from "../../tastyworks/device-challenge"
import type HttpClient from "../../tastyworks/http"
import {
  ATTEMPTS_REMAINING_HEADER,
  DEVICE_CHALLENGE_TOKEN_HEADER,
  InvalidAnswerError,
  hasRecoveryCodeMethodAvailable,
  isInvalidSecurityQuestionResponse,
  toJsonHelper,
  twoFactorType,
} from "../../tastyworks/http"
import { toFormData } from "../../tastyworks/request"
import {
  BaseResponse,
  parseErrorContainer,
  parseSingleResponse,
} from "../../tastyworks/response"

export class DeviceChallengeService {
  private readonly httpClient: HttpClient

  constructor(baseHttpClient: HttpClient) {
    this.httpClient = baseHttpClient.nested("device-challenge")
  }

  readonly challengeDetails = async ({
    challengeToken,
    answer,
  }: {
    challengeToken: string
    answer?: string
  }) => {
    const url = this.httpClient.baseUrl.toUrl("")

    const castleTokenHeader = await this.httpClient.session.getCastleHeaders()

    const headers: HeadersInit = {
      [DEVICE_CHALLENGE_TOKEN_HEADER]: challengeToken,
      ...castleTokenHeader,
    }
    const body = answer ? toFormData({ answer }) : undefined
    const fetchResponse = await this.httpClient.fetcher(url, {
      body,
      headers,
      method: "POST",
    })

    const helper = await toJsonHelper(fetchResponse)

    const response = formatResponse(helper, fetchResponse)

    if (isInvalidSecurityQuestionResponse(response.errorContainer)) {
      const attemptsRemaining = fetchResponse.headers.get(
        ATTEMPTS_REMAINING_HEADER
      )

      throw new InvalidAnswerError(
        response.errorContainer.message,
        attemptsRemaining
      )
    }

    return response
  }

  readonly resendOtp = async (challengeToken: string) => {
    const castleTokenHeader = await this.httpClient.session.getCastleHeaders()

    const headers: HeadersInit = {
      [DEVICE_CHALLENGE_TOKEN_HEADER]: challengeToken,
      ...castleTokenHeader,
    }
    const helper = await this.httpClient.postJson(
      "resend-otp",
      undefined,
      undefined,
      headers
    )

    const response = new BaseResponse()
    parseErrorContainer(helper, response)

    return response
  }
}

function formatResponse(helper: JsonHelper, fetchResponse: Response) {
  const response = parseSingleResponse(
    helper,
    DEVICE_CHALLENGE_DESER.toParser(DeviceChallenge)
  )

  if (response.data) {
    const otpHeader = twoFactorType(fetchResponse)
    response.data.twoFactorType = otpHeader ?? TWO_FACTOR_TYPE.EMAIL
    response.data.hasRecoveryCodesAvailable =
      hasRecoveryCodeMethodAvailable(fetchResponse)
  }

  return response
}
